import ApiRoutes from "../../routes/routeConstants/apiRoutes";
import axiosInstance from "../../interceptor/axiosInstance";
import { ScanCenter } from "../../models/ScanCenter/scanCenter.model";
import { deserialize, serialize } from "serializr";
import Notification from "../../shared/components/Notification";
import { NotificationTypes } from "../../enums/notificationTypes";
import { SummaryFilterParams } from "../../models/SummaryFilterParams/summaryFilterParams.model";
import { Summary } from "../../models/Summary/summary.model";
import { UserRoleEnum } from "../../enums/userRole.enum";
import { Admin } from "../../models/Admin/admin.model";
import { AdminTypeEnum } from "../../enums/AdminType.enum";

export default class ScanCenterService {
  static fetchScanCenters(
    onSuccess: Function,
    onError: Function,
    onFinal: () => void,
    userRole: UserRoleEnum = UserRoleEnum.ADMIN,
    includeScanCenterAdmins: boolean = false
  ) {
    const API_URL = userRole === UserRoleEnum.ADMIN ? ApiRoutes.SCAN_CENTERS : ApiRoutes.DOCTOR_SCAN_CENTERS;
    axiosInstance
      .get(API_URL)
      .then((response) => {
        let admins: any = response.data["related_scan_center_admins"] ?? [];
        const scanCenters = (deserialize(
          ScanCenter,
          response.data["scan_centers"]
        ) as ScanCenter[]) ?? [];
        scanCenters.forEach((scanCenter) => {
          scanCenter.type = UserRoleEnum.SCAN_CENTER;
        });
        admins = admins.map((admin: any) => {
          const scanCenter = new ScanCenter();
          scanCenter.id = admin.id;
          scanCenter.name = admin.doctor_name;
          scanCenter.type = AdminTypeEnum.SCAN_CENTER_ADMIN;
          scanCenter.email = admin.email;
          scanCenter.mobileNumber = admin.mobile_number;
          return scanCenter;
        });
        let result = [...scanCenters];
        if(includeScanCenterAdmins) {
          result = [...scanCenters, ...admins];
        }
        onSuccess(result);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static showScanCenter(
    scanCenterId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.SCAN_CENTERS + "/" + scanCenterId;
    axiosInstance
      .get(API_URL)
      .then((response) => {
        const scanCenter = deserialize(
          ScanCenter,
          response.data["scan_center"]
        );
        onSuccess(scanCenter);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static showScanCenterSummary(
    filterParams: SummaryFilterParams,
    scanCenterId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const params = serialize(filterParams);
    const API_URL = ApiRoutes.ADMIN_SCAN_CENTER_SUMMARY.replace(
      ":scanCenterId",
      scanCenterId.toString()
    );
    axiosInstance
      .get(API_URL, {
        params,
      })
      .then((response) => {
        const scanCenterSummary = deserialize(Summary, response.data);
        onSuccess(scanCenterSummary);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static createScanCenter(
    scanCenter: ScanCenter,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const scanCenterJSON = { scan_center: serialize(scanCenter) };
    axiosInstance
      .post(ApiRoutes.SCAN_CENTER_SIGNUP, scanCenterJSON)
      .then((response) => {
        Notification({
          message: "Scan Center created!",
          type: NotificationTypes.SUCCESS,
        });
        const scanCenter = deserialize(
          ScanCenter,
          response.data["scan_center"]
        );
        onSuccess(scanCenter);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static updateScanCenter(
    scanCenter: ScanCenter,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.SCAN_CENTERS + "/" + scanCenter.id;
    const scanCenterJSON = { scan_center: serialize(scanCenter) };
    axiosInstance
      .put(API_URL, scanCenterJSON)
      .then((response) => {
        Notification({
          message: "Scan Center updated!",
          type: NotificationTypes.SUCCESS,
        });
        const scanCenter = deserialize(
          ScanCenter,
          response.data["scan_center"]
        );
        onSuccess(scanCenter);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static deleteScanCenter(
    scanCenterId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.SCAN_CENTERS + "/" + scanCenterId;
    axiosInstance
      .delete(API_URL)
      .then((response) => {
        onSuccess();
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  /** Doctors eligible for whitelist + current selection (admin API). */
  static fetchAssignableDoctors(
    scanCenterId: number,
    onSuccess: (data: {
      eligible_doctors: Array<{
        id: number;
        doctor_name: string;
        email?: string;
        selected?: boolean;
      }>;
      selected_doctor_ids: number[];
    }) => void,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.ADMIN_SCAN_CENTER_ASSIGNABLE_DOCTORS.replace(
      ":scanCenterId",
      String(scanCenterId)
    );
    axiosInstance
      .get(API_URL)
      .then((response) => {
        onSuccess(response.data);
      })
      .catch(() => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static updateAssignableDoctors(
    scanCenterId: number,
    doctorIds: number[],
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.ADMIN_SCAN_CENTER_ASSIGNABLE_DOCTORS.replace(
      ":scanCenterId",
      String(scanCenterId)
    );
    axiosInstance
      .put(API_URL, { doctor_ids: doctorIds })
      .then(() => {
        Notification({
          message: "Assignable doctors updated",
          type: NotificationTypes.SUCCESS,
        });
        onSuccess();
      })
      .catch(() => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static fetchScanCenterFeatures(
    scanCenterId: number,
    onSuccess: (data: {
      eligible_features: Array<{ id: number; name: string; selected?: boolean }>;
      selected_feature_ids: number[];
    }) => void,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.ADMIN_SCAN_CENTER_FEATURES.replace(
      ":scanCenterId",
      String(scanCenterId)
    );
    axiosInstance
      .get(API_URL)
      .then((response) => {
        onSuccess(response.data);
      })
      .catch(() => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static updateScanCenterFeatures(
    scanCenterId: number,
    featureIds: number[],
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.ADMIN_SCAN_CENTER_FEATURES.replace(
      ":scanCenterId",
      String(scanCenterId)
    );
    axiosInstance
      .put(API_URL, { feature_ids: featureIds })
      .then(() => {
        onSuccess();
      })
      .catch(() => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }
}
